<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-7xl mx-auto space-y-8">
        
        <!-- HEADER -->
        <div class="mb-8">
          <h1 class="text-2xl font-black text-slate-800 tracking-tight">Fornecedores</h1>
          <p class="text-sm text-slate-500 font-medium">Gestão e controle de fornecedores cadastrados no sistema</p>
        </div>

        <!-- LISTAGEM -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <!-- BARRA DE FERRAMENTAS DA TABELA -->
            <div class="bg-slate-700 px-6 py-4 flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <v-icon color="white" small>mdi-format-list-bulleted</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">RELAÇÃO DE FORNECEDORES</h2>
                </div>
                
                <div class="flex items-center gap-4 flex-1 justify-end">
                  <div class="flex-none">
                    <button 
                      v-if="isAdmin"
                      @click="$router.push('/providers-manager/create')" 
                      class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                    >
                      <v-icon color="white" small>mdi-plus</v-icon>
                      <span class="font-bold text-sm uppercase">NOVO</span>
                    </button>
                  </div>
                  
                  <div class="hidden md:flex relative w-80">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                    </div>
                    <input 
                      v-model="search"
                      placeholder="PESQUISAR FORNECEDOR..."
                      class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                    >
                  </div>
                </div>
            </div>

            <!-- SEARCH MOBILE -->
            <div class="md:hidden p-4 bg-slate-50 border-b border-slate-100">
               <v-text-field
                v-model="search"
                placeholder="PESQUISAR..."
                solo flat dense hide-details
                prepend-inner-icon="mdi-magnify"
                background-color="white"
                class="custom-select"
              />
            </div>

            <div class="p-0">
                <v-data-table
                  :headers="headers"
                  :items="data"
                  :search="search"
                  :page.sync="page"
                  :items-per-page="itemsPerPage"
                  @page-count="pageCount = $event"
                  :loading="loader"
                  loading-text="CARREGANDO DADOS..."
                  class="custom-table"
                  hide-default-footer
                >
                  <template v-slot:item.provider_name="{ item }">
                    <div class="flex items-center gap-3 py-2">
                      <v-avatar size="36" class="border border-slate-100 bg-slate-50">
                        <img :src="item.provider_logo ? $store.state.urlImages + item.provider_logo : require('@/assets/no-image.jpg')" alt="Logo" class="object-cover">
                      </v-avatar>
                      <div class="flex flex-col">
                        <span class="text-sm font-bold text-slate-800 block tracking-tight">{{ item.provider_name }}</span>
                        <span class="text-[10px] text-slate-400 font-bold uppercase">{{ item.provider_document }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.provider_mail="{ item }">
                    <span class="text-xs text-slate-600 font-medium lowercase">{{ item.provider_mail }}</span>
                  </template>

                  <template v-slot:item.provider_tel="{ item }">
                    <span class="text-xs text-slate-500 font-semibold">{{ item.provider_tel || item.provider_cel }}</span>
                  </template>

                  <template v-slot:item.action="{ item }">
                    <div class="flex justify-end gap-2">
                      <button 
                        @click="$router.push(`/providers-manager/edit/${item.provider_id}`)"
                        class="p-2 hover:bg-orange-50 text-slate-400 hover:text-orange-500 rounded-lg transition-all"
                        title="Editar"
                      >
                        <v-icon small color="currentColor">mdi-pencil-outline</v-icon>
                      </button>
                      <button 
                        @click="deleteItem(item.provider_id)"
                        class="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded-lg transition-all"
                        title="Excluir"
                      >
                        <v-icon small color="currentColor">mdi-trash-can-outline</v-icon>
                      </button>
                    </div>
                  </template>

                  <template v-slot:no-data>
                    <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                      <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                      <p class="mt-2 text-sm font-medium">Nenhum fornecedor encontrado</p>
                    </div>
                  </template>
                </v-data-table>

                <!-- Custom Pagination Footer -->
                <div class="bg-slate-50 px-6 py-8 border-t border-slate-200 flex flex-col items-center justify-center gap-6 text-[10px] text-slate-500 font-bold uppercase tracking-widest w-full">
                    <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                    
                    <div class="flex gap-2">
                        <button 
                          @click="page > 1 ? page-- : null" 
                          :disabled="page === 1"
                          class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-1"
                        >
                          <v-icon x-small>mdi-chevron-left</v-icon>
                          ANTERIOR
                        </button>
                        
                        <div class="flex gap-1">
                          <button 
                            v-for="p in visiblePages" 
                            :key="p"
                            @click="page = p"
                            :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-md' : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-600'"
                            class="w-10 h-10 border rounded-lg transition-all font-bold flex items-center justify-center"
                          >
                            {{ p }}
                          </button>
                        </div>

                        <button 
                          @click="page < pageCount ? page++ : null" 
                          :disabled="page === pageCount"
                          class="px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-1"
                        >
                          PRÓXIMO
                          <v-icon x-small>mdi-chevron-right</v-icon>
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <!-- NOTIFICAÇÕES -->
        <v-snackbar
          v-model="snack.show"
          :color="snack.color"
          timeout="3000"
          rounded="lg"
          elevation="24"
        >
          <div class="flex items-center gap-3">
            <v-icon color="white" small>
              {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
          </div>
        </v-snackbar>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "Nome/Razão Social", value: "provider_name" },
        { text: "CPF/CNPJ", value: "provider_document" },
        { text: "Telefone", value: "provider_tel" },
        { text: "E-mail", value: "provider_mail" },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ]
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.getUserData.group_id === 1;
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get("/providers")
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/providers/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
.custom-table ::v-deep table { 
  background-color: transparent !important; 
}

.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 900 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.1em !important;
  background-color: #f8fafc !important;
  border-bottom: 2px solid #f1f5f9 !important;
  height: 52px !important;
}

.custom-table ::v-deep td { 
  border-bottom: 1px solid #f8fafc !important;
  color: #475569 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #f8fafc !important;
}

.custom-select ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 44px !important;
}

.custom-select ::v-deep input {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  text-transform: uppercase !important;
}

.bg-slate-700 { background-color: #333333 !important; }
</style>
